// One voice agent — /voice-agents/:agentId. What needs a person (no number,
// no instructions, no tool, tools without effect, a line silent for days),
// its state in numbers, then what it says (greeting, phone instructions),
// what it can do (tools limited to the account, knowledge base), the numbers
// it answers, a test call in the browser, its name and voice, its last calls.
// Edits are saved together from the sticky bar; delete lives in « ⋯ ».
// API: GET /api/voice-agents/{id}, GET /api/ai-voice/voice-tools,
// PUT /api/voice-agents/{id}, DELETE /api/voice-agents/{id},
// PUT /api/voice-numbers/{number}/agent (route / unroute a number).

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AudioLines, ChevronRight, CircleAlert, Clock, FileText, MessageSquareText, Phone, PhoneCall, PhoneOff,
    Trash2, Wrench,
} from 'lucide-react';
import {
    Badge, Banner, Card, ConfirmDialog, EmptyState, Field, IconChip, Kpi, OverflowMenu, PageHeader, Switch, Toast,
} from '../../design/ui';
import VoicePreview from './VoicePreview';
import {
    DEFAULT_VOICE, GROUP_LABELS, LANGUAGES, LANGUAGE_LABELS, REALTIME_VOICES, SPEEDS, SPEED_LABELS, TOOL_GROUPS,
    canManage, deleteAgent, fail, formatDay, formatDuration, getAgent, getVoiceTools, setAgent, updateAgent,
} from './api';
import { formatCallDay } from '../../utils/voiceFormat';
import { agentPageAttention } from './voiceModel';
import { AttentionCard, SaveBar, durationTile, formatCount, pluralT, scrollToId } from './VoiceKit';
import { formatPhone, numberLabel } from './phoneFormat';
import './VoiceSetup.css';

const formOf = (agent) => ({
    name: agent.name || '',
    greeting: agent.greeting || '',
    instructions: agent.instructions || '',
    prosody: agent.prosody || '',
    voice: agent.voice || '',
    speed: agent.speed !== null && agent.speed !== undefined ? String(agent.speed) : '',
    language: agent.language || '',
    memory_enabled: Boolean(agent.memory_enabled),
    selected_voice_tool_ids: agent.selected_voice_tool_ids || [],
    knowledge_agent_id: agent.knowledge_agent_id || '',
});

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const VoiceAgentPage = () => {
    const { agentId } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const lang = i18n?.language || 'fr';
    const tp = pluralT(t);
    const manage = canManage();
    const [agent, setAgentView] = useState(null);
    const [tools, setTools] = useState([]);
    const [form, setForm] = useState(null);
    const [saved, setSaved] = useState(null);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null);
    const [saving, setSaving] = useState(false);
    const [routing, setRouting] = useState(false);
    const [confirmTemplate, setConfirmTemplate] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [unrouting, setUnrouting] = useState(null);
    const [showProsody, setShowProsody] = useState(false);

    const apply = (view) => {
        const next = formOf(view);
        setAgentView(view);
        setForm(next);
        setSaved(next);
        setShowProsody(Boolean(next.prosody));
    };

    useEffect(() => {
        let alive = true;
        Promise.all([getAgent(agentId), getVoiceTools()])
            .then(([agentRes, toolsRes]) => {
                if (!alive) return;
                apply(agentRes.data);
                setTools(toolsRes.data.voice_tools || []);
            })
            .catch((err) => alive && setError(fail(err, t('voiceSetup.agent.loadError', 'L’agent vocal n’a pas pu être chargé.'))));
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId]);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const dirty = Boolean(form && saved && JSON.stringify(form) !== JSON.stringify(saved));
    const selected = useMemo(() => new Set(form?.selected_voice_tool_ids || []), [form]);

    const toolLabel = (functionName) => {
        const tool = tools.find((x) => x.function_name === functionName);
        return t(`voiceSetup.tools.${functionName}.label`, tool?.label_fr || tool?.name || functionName);
    };

    const groups = useMemo(() => {
        const visible = tools.filter((tool) => tool.offered || selected.has(tool.id));
        const byGroup = TOOL_GROUPS.map((group) => ({
            group,
            items: visible.filter((tool) => tool.offered && (tool.group || 'other') === group),
        })).filter((g) => g.items.length > 0);
        const leftovers = visible.filter((tool) => !tool.offered);
        if (leftovers.length) byGroup.push({ group: 'unavailable', items: leftovers });
        return byGroup;
    }, [tools, selected]);

    if (error && !agent) {
        return (
            <div className="u-page voice-v2">
                <section className="u-card">
                    <EmptyState
                        icon={CircleAlert}
                        title={error}
                        action={<Link className="u-btn u-btn--secondary u-btn--sm" to="/voice-agents">{t('voiceSetup.agents.back', 'Retour aux agents vocaux')}</Link>}
                    />
                </section>
            </div>
        );
    }
    if (!agent || !form) {
        return (
            <div className="u-page voice-v2" aria-busy="true">
                <div className="voice-v2__header-skeleton">
                    <span className="u-skeleton voice-v2__line-skeleton" />
                    <span className="u-skeleton voice-v2__title-skeleton" />
                </div>
                <div className="u-well">
                    <div className="u-kpi-row" aria-hidden="true">
                        {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton voice-v2__kpi-skeleton" />)}
                    </div>
                    <section className="u-card voice-v2__list-skeleton">
                        {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton" />)}
                    </section>
                </div>
            </div>
        );
    }

    const recommended = tools.filter((tool) => tool.offered && tool.recommended).map((tool) => tool.id);
    const missingRecommended = recommended.filter((id) => !selected.has(id));
    const offeredCount = tools.filter((tool) => tool.offered).length;
    const activeCount = tools.filter((tool) => tool.offered && selected.has(tool.id)).length;
    const knowledgeOn = tools.some((tool) => tool.function_name === 'retrieve_knowledge' && selected.has(tool.id));
    const assignable = (agent.account_numbers || []).filter((n) => n.agent_id !== agent.id);
    const numbersList = agent.numbers.map(numberLabel).join(', ');
    const recentCalls = agent.recent_calls || [];
    const recentDurations = recentCalls.map((call) => call.duration_seconds).filter((value) => typeof value === 'number');
    const avgDuration = recentDurations.length ? recentDurations.reduce((sum, value) => sum + value, 0) / recentDurations.length : null;

    const toggleTool = (id, on) => set({
        selected_voice_tool_ids: on ? [...form.selected_voice_tool_ids, id] : form.selected_voice_tool_ids.filter((x) => x !== id),
    });

    const applyTemplate = () => {
        set({
            greeting: form.greeting || t('voiceSetup.agent.templateGreeting', 'Bonjour, vous êtes bien au service client. Je suis l’assistant vocal : comment puis-je vous aider ?'),
            instructions: t('voiceSetup.agent.templateInstructions', 'Tu réponds au téléphone pour le service client. Les appelants sont des destinataires de colis ou des clients professionnels.\n\n- Présente-toi brièvement, puis demande comment tu peux aider.\n- Pour un colis : demande le numéro de suivi, relis-le à l’appelant, puis consulte le suivi. Donne le dernier statut en une ou deux phrases, sans jargon.\n- Si le problème demande une action (colis perdu, abîmé, retour), propose d’ouvrir une demande : demande l’adresse e-mail de l’appelant, épelle-la pour la confirmer, puis crée le ticket.\n- Ne promets jamais un délai ou un remboursement que tu ne peux pas vérifier.\n- Si tu n’as pas compris, demande de répéter. N’invente rien.\n- Quand la demande est traitée ou que l’appelant dit au revoir, salue-le et raccroche.'),
        });
        setConfirmTemplate(false);
    };

    const save = async () => {
        if (!form.name.trim()) {
            setToast({ tone: 'danger', text: t('voiceSetup.agent.nameRequired', 'Donnez un nom à l’agent vocal.') });
            return;
        }
        setSaving(true);
        setToast(null);
        try {
            const res = await updateAgent(agentId, {
                name: form.name.trim(),
                greeting: form.greeting,
                instructions: form.instructions,
                prosody: form.prosody,
                voice: form.voice || null,
                speed: form.speed === '' ? null : parseFloat(form.speed),
                language: form.language || null,
                memory_enabled: form.memory_enabled,
                selected_voice_tool_ids: form.selected_voice_tool_ids,
                knowledge_agent_id: form.knowledge_agent_id || null,
            });
            apply(res.data);
            setToast({ tone: 'success', text: t('voiceSetup.agent.saved', 'Agent vocal enregistré.') });
        } catch (err) {
            setToast({ tone: 'danger', text: fail(err, t('voiceSetup.agent.saveError', 'L’agent vocal n’a pas pu être enregistré.')) });
        } finally {
            setSaving(false);
        }
    };

    const route = async (number, target) => {
        setRouting(true);
        setToast(null);
        try {
            await setAgent(number.phone_number, target);
            const res = await getAgent(agentId);
            // Keep unsaved edits: only the number data comes from the server.
            setAgentView((prev) => ({ ...prev, numbers: res.data.numbers, account_numbers: res.data.account_numbers }));
            setToast({
                tone: 'success',
                text: target
                    ? t('voiceUi.agent.routed', 'L’agent répond maintenant sur le {{number}}.', { number: numberLabel(number) })
                    : t('voiceUi.agent.unrouted', 'L’agent ne répond plus sur le {{number}}.', { number: numberLabel(number) }),
            });
        } catch (err) {
            setToast({ tone: 'danger', text: fail(err, t('voiceSetup.agent.routeError', 'Le numéro n’a pas pu être changé.')) });
        } finally {
            setRouting(false);
            setUnrouting(null);
        }
    };

    const remove = async () => {
        setDeleting(true);
        try {
            await deleteAgent(agentId);
            navigate('/voice-agents');
        } catch (err) {
            setToast({ tone: 'danger', text: fail(err, t('voiceSetup.agent.deleteError', 'L’agent vocal n’a pas pu être supprimé.')) });
            setConfirmDelete(false);
            setDeleting(false);
        }
    };

    const attention = agentPageAttention({ agent, form, tools }).map((item) => {
        if (item.kind === 'noNumber') {
            let action = null;
            if (manage) {
                action = assignable.length
                    ? { label: t('voiceUi.agent.chooseNumber', 'Choisir un numéro'), onClick: () => scrollToId('voice-agent-numbers') }
                    : { label: t('voiceSetup.agent.getNumber', 'Obtenir un numéro'), onClick: () => navigate('/voice-numbers?buy=1') };
            }
            return {
                ...item,
                title: t('voiceUi.agent.attNoNumber', 'L’agent ne reçoit pas d’appels'),
                detail: t('voiceUi.agent.attNoNumberDetail', 'Aucun numéro ne renvoie vers lui : vos clients ne peuvent pas le joindre.'),
                action,
            };
        }
        if (item.kind === 'silent') {
            return {
                ...item,
                title: t('voiceUi.agent.attSilent', 'Aucun appel depuis le {{date}}', { date: formatDay(item.last, lang) }),
                detail: tp(
                    'voiceUi.attention.silentDetail',
                    '{{count}} jour sans appel sur le {{number}} : appelez-le pour vérifier que l’agent décroche.',
                    '{{count}} jours sans appel sur le {{number}} : appelez-le pour vérifier que l’agent décroche.',
                    { count: item.days, number: numbersList },
                ),
            };
        }
        if (item.kind === 'noInstructions') {
            return {
                ...item,
                title: t('voiceUi.agent.attNoInstructions', 'L’agent n’a pas de consignes'),
                detail: t('voiceSetup.agent.noInstructions', 'Sans instructions, l’agent improvise : partez du modèle ou écrivez vos consignes.'),
                action: manage ? { label: t('voiceUi.agent.writeInstructions', 'Écrire les consignes'), onClick: () => scrollToId('voice-agent-speech') } : null,
            };
        }
        if (item.kind === 'noKnowledge') {
            return {
                ...item,
                title: t('voiceUi.agent.attNoKnowledge', 'La base de connaissances n’est pas choisie'),
                detail: t('voiceUi.agent.attNoKnowledgeDetail', 'L’outil « Base de connaissances » est activé mais ne consulte aucune base : il ne trouve rien.'),
                action: { label: t('voiceUi.agent.seeTools', 'Voir les outils'), onClick: () => scrollToId('voice-agent-tools') },
            };
        }
        if (item.kind === 'noTools') {
            return {
                ...item,
                title: t('voiceUi.agent.attNoTools', 'Aucun outil activé'),
                detail: t('voiceSetup.agent.noTools', 'Sans outil, l’agent ne peut ni consulter un suivi, ni ouvrir une demande, ni raccrocher : activez au moins la sélection recommandée.'),
                action: { label: t('voiceUi.agent.seeTools', 'Voir les outils'), onClick: () => scrollToId('voice-agent-tools') },
            };
        }
        return {
            ...item,
            title: tp('voiceUi.agent.attUnavailable', '{{count}} outil activé sans effet pour ce compte', '{{count}} outils activés sans effet pour ce compte', { count: item.count }),
            detail: t('voiceUi.agent.attUnavailableDetail', 'Intégration absente ou autre métier : désactivez-les dans « Ce que l’agent sait faire ».'),
            action: { label: t('voiceUi.agent.seeTools', 'Voir les outils'), onClick: () => scrollToId('voice-agent-tools') },
        };
    });

    const subtitle = [
        agent.numbers.length
            ? t('voiceSetup.agent.answers', 'Répond au {{numbers}}', { numbers: numbersList })
            : t('voiceUi.agent.subtitleNoNumber', 'Aucun numéro'),
        tp('voiceUi.agents.scopeCalls', '{{count}} appel sur 30 jours', '{{count}} appels sur 30 jours', { count: agent.calls_30d || 0 }),
        agent.updated_at && t('voiceUi.agent.updated', 'modifié le {{date}}', { date: formatDay(agent.updated_at, lang) }),
    ].filter(Boolean).join(' · ');

    const speedOptions = SPEEDS.includes(form.speed) || form.speed === '' ? SPEEDS : [...SPEEDS, form.speed];
    const voiceOptions = REALTIME_VOICES.includes(form.voice) || form.voice === '' ? REALTIME_VOICES : [...REALTIME_VOICES, form.voice];
    const languageOptions = LANGUAGES.includes(form.language) || form.language === '' ? LANGUAGES : [...LANGUAGES, form.language];

    const renderKpis = () => (
        <div className="u-kpi-row">
            <Kpi
                icon={Phone}
                tone={agent.numbers.length ? 'success' : 'warning'}
                label={t('voiceUi.agent.kpiNumbers', 'Numéros')}
                value={formatCount(agent.numbers.length, lang)}
                context={agent.numbers.length
                    ? t('voiceUi.agent.kpiNumbersOn', 'répond au {{numbers}}', { numbers: numbersList })
                    : t('voiceUi.agent.kpiNumbersOff', 'ne reçoit pas d’appels')}
                contextTone={agent.numbers.length ? undefined : 'warning'}
                onClick={() => scrollToId('voice-agent-numbers')}
            />
            <Kpi
                icon={PhoneCall}
                tone="info"
                label={t('voiceUi.agents.kpiCalls', 'Appels sur 30 jours')}
                value={formatCount(agent.calls_30d || 0, lang)}
                context={agent.last_call_at
                    ? t('voiceUi.agent.kpiLastCall', 'dernier le {{date}}', { date: formatDay(agent.last_call_at, lang) })
                    : t('voiceUi.noCallYet', 'aucun appel reçu')}
                onClick={() => navigate('/voice-tickets')}
                title={t('voiceUi.openCalls', 'Ouvrir les appels')}
            />
            {offeredCount > 0 && (
                <Kpi
                    icon={Wrench}
                    tone={activeCount ? 'success' : 'warning'}
                    label={t('voiceUi.agent.kpiTools', 'Outils actifs')}
                    value={`${activeCount} / ${offeredCount}`}
                    context={missingRecommended.length
                        ? tp('voiceUi.agent.kpiToolsMissing', '{{count}} recommandé à activer', '{{count}} recommandés à activer', { count: missingRecommended.length })
                        : t('voiceUi.agent.kpiToolsOk', 'recommandés activés')}
                    contextTone={missingRecommended.length ? 'warning' : undefined}
                    meter={{ value: activeCount, max: offeredCount, tone: activeCount ? undefined : 'warning', label: t('voiceUi.agent.kpiTools', 'Outils actifs') }}
                    onClick={() => scrollToId('voice-agent-tools')}
                />
            )}
            {avgDuration !== null && (
                <Kpi
                    icon={Clock}
                    label={t('voiceUi.kpiAvgDuration', 'Durée moyenne')}
                    {...durationTile(avgDuration)}
                    context={tp('voiceUi.agent.kpiAvgContext', 'sur le dernier appel', 'sur les {{count}} derniers appels', { count: recentDurations.length })}
                />
            )}
        </div>
    );

    const renderSpeech = () => (
        <Card
            id="voice-agent-speech"
            icon={MessageSquareText}
            title={t('voiceSetup.agent.speechTitle', 'Accueil et instructions')}
            description={t('voiceSetup.agent.speechDesc', 'Ce que l’agent dit en décrochant, et comment il mène l’appel.')}
            actions={manage ? (
                <button
                    type="button"
                    className="u-btn u-btn--secondary u-btn--sm"
                    onClick={() => (form.instructions.trim() ? setConfirmTemplate(true) : applyTemplate())}
                >
                    <FileText aria-hidden="true" />
                    {t('voiceSetup.agent.useTemplate', 'Partir du modèle logistique')}
                </button>
            ) : undefined}
        >
            <div className="u-form">
                <Field
                    label={t('voiceSetup.agent.greeting', 'Message d’accueil')}
                    htmlFor="voice-agent-greeting"
                    help={t('voiceUi.agent.greetingHelp', 'La première phrase de chaque appel, dite telle quelle.')}
                >
                    <textarea
                        id="voice-agent-greeting"
                        className="u-textarea"
                        rows={2}
                        value={form.greeting}
                        disabled={!manage}
                        onChange={(e) => set({ greeting: e.target.value })}
                        placeholder={t('voiceSetup.agent.greetingPlaceholder', 'Bonjour, vous êtes bien au service client. Comment puis-je vous aider ?')}
                    />
                </Field>
                <Field
                    label={t('voiceSetup.agent.instructions', 'Instructions au téléphone')}
                    htmlFor="voice-agent-instructions"
                    help={t('voiceUi.agent.instructionsHelp', 'Courtes et faites pour l’oral : qui appelle, ce que l’agent fait, ce qu’il ne promet jamais, quand il raccroche.')}
                >
                    <textarea
                        id="voice-agent-instructions"
                        className="u-textarea"
                        rows={11}
                        value={form.instructions}
                        disabled={!manage}
                        onChange={(e) => set({ instructions: e.target.value })}
                        placeholder={t('voiceSetup.agent.instructionsPlaceholderOwn', 'Consignes courtes, faites pour l’oral : qui appelle, ce que l’agent fait, ce qu’il ne promet jamais, quand il raccroche.')}
                    />
                </Field>
                <div>
                    <button
                        type="button"
                        className="u-btn u-btn--ghost u-btn--sm voice-v2__disclosure"
                        aria-expanded={showProsody}
                        onClick={() => setShowProsody(!showProsody)}
                    >
                        <ChevronRight aria-hidden="true" />
                        {t('voiceSetup.agent.prosody', 'Style de parole (avancé)')}
                    </button>
                </div>
                {showProsody && (
                    <Field label={t('voiceSetup.agent.prosodyLabel', 'Ton, rythme, pauses')} htmlFor="voice-agent-prosody">
                        <textarea
                            id="voice-agent-prosody"
                            className="u-textarea"
                            rows={3}
                            value={form.prosody}
                            disabled={!manage}
                            onChange={(e) => set({ prosody: e.target.value })}
                            placeholder={t('voiceSetup.agent.prosodyPlaceholder', 'Ex. : ton chaleureux et posé, phrases courtes, laisse l’appelant finir avant de répondre.')}
                        />
                    </Field>
                )}
            </div>
        </Card>
    );

    const renderTools = () => (
        <Card
            id="voice-agent-tools"
            icon={Wrench}
            title={t('voiceSetup.agent.toolsTitle', 'Ce que l’agent sait faire')}
            description={t('voiceSetup.agent.toolsCount', '{{active}} outil(s) activé(s) sur {{total}}', { active: activeCount, total: offeredCount })}
            actions={manage && missingRecommended.length > 0 ? (
                <button
                    type="button"
                    className="u-btn u-btn--secondary u-btn--sm"
                    onClick={() => set({ selected_voice_tool_ids: [...form.selected_voice_tool_ids, ...missingRecommended] })}
                >
                    {t('voiceSetup.agent.recommended', 'Activer la sélection recommandée')}
                </button>
            ) : undefined}
            flush
        >
            {groups.map(({ group, items }) => (
                <div key={group} className="voice-v2__tool-group">
                    <div className="voice-v2__group-head">
                        <span className="u-overline">{t(`voiceSetup.groups.${group}`, GROUP_LABELS[group] || group)}</span>
                        {group === 'unavailable' && (
                            <span className="voice-v2__group-hint">
                                {t('voiceUi.agent.unavailableHint', 'Activés sur cet agent mais sans effet pour ce compte (intégration absente ou autre métier) : désactivez-les.')}
                            </span>
                        )}
                    </div>
                    <ul className="u-list">
                        {items.map((tool) => {
                            const id = `voice-tool-${tool.id}`;
                            return (
                                <li key={tool.id} className="u-row">
                                    <div className="u-row__main">
                                        <label className="u-row__title" htmlFor={id}>
                                            <span>{toolLabel(tool.function_name)}</span>
                                            {tool.recommended && <Badge tone="info">{t('voiceSetup.agent.recommendedTag', 'Recommandé')}</Badge>}
                                        </label>
                                        <div className="u-row__meta u-row__meta--wrap">
                                            {t(`voiceSetup.tools.${tool.function_name}.hint`, tool.summary_fr || tool.description || '')}
                                        </div>
                                    </div>
                                    <div className="u-row__side">
                                        <Switch id={id} checked={selected.has(tool.id)} disabled={!manage} onChange={(on) => toggleTool(tool.id, on)} />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ))}
            {knowledgeOn && (
                <div className="voice-v2__inset">
                    <Field
                        label={t('voiceSetup.agent.knowledge', 'Base de connaissances consultée')}
                        htmlFor="voice-agent-knowledge"
                        error={!form.knowledge_agent_id ? t('voiceSetup.agent.knowledgeMissing', 'Choisissez une base : sinon l’outil ne trouve rien.') : undefined}
                    >
                        <select
                            id="voice-agent-knowledge"
                            className="u-select"
                            value={form.knowledge_agent_id}
                            disabled={!manage}
                            onChange={(e) => set({ knowledge_agent_id: e.target.value })}
                        >
                            <option value="">{t('voiceSetup.agent.knowledgeNone', 'Aucune')}</option>
                            {(agent.knowledge_sources || []).map((k) => (
                                <option key={k.id} value={k.id}>{t('voiceSetup.agent.knowledgeOf', 'Celle de l’agent texte « {{name}} »', { name: k.name })}</option>
                            ))}
                        </select>
                    </Field>
                </div>
            )}
        </Card>
    );

    const renderNumbers = () => (
        <Card
            id="voice-agent-numbers"
            icon={Phone}
            title={t('voiceUi.agent.numbersTitle', 'Numéros')}
            description={agent.numbers.length
                ? t('voiceUi.agent.numbersDesc', 'Les appels vers ces numéros arrivent sur cet agent.')
                : t('voiceUi.agent.numbersEmpty', 'Aucun numéro ne renvoie encore vers cet agent.')}
            footer={(
                <Link className="u-btn u-btn--ghost u-btn--sm" to={assignable.length || agent.numbers.length ? '/voice-numbers' : '/voice-numbers?buy=1'}>
                    {assignable.length || agent.numbers.length
                        ? t('voiceSetup.agent.manageNumbers', 'Gérer les numéros')
                        : t('voiceSetup.agent.getNumber', 'Obtenir un numéro')}
                    <ChevronRight aria-hidden="true" />
                </Link>
            )}
            flush
        >
            {agent.numbers.length > 0 && (
                <ul className="u-list">
                    {agent.numbers.map((n) => (
                        <li key={n.phone_number} className="u-row">
                            <IconChip icon={Phone} tone="success" size="sm" />
                            <div className="u-row__main">
                                <div className="u-row__title u-num">{numberLabel(n)}</div>
                            </div>
                            {manage && (
                                <div className="u-row__side">
                                    <OverflowMenu
                                        label={t('voiceUi.agent.numberActions', 'Actions du {{number}}', { number: numberLabel(n) })}
                                        items={[{
                                            label: t('voiceUi.agent.unrouteItem', 'Ne plus répondre sur ce numéro'),
                                            icon: PhoneOff,
                                            danger: true,
                                            disabled: routing,
                                            onClick: () => setUnrouting(n),
                                        }]}
                                    />
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {manage && assignable.length > 0 && (
                <div className="voice-v2__inset">
                    <Field
                        label={t('voiceSetup.agent.assign', 'Répondre aussi sur')}
                        htmlFor="voice-agent-assign"
                        help={t('voiceUi.agent.assignHelp', 'Un numéro déjà relié à un autre agent passe sur celui-ci.')}
                    >
                        <select
                            id="voice-agent-assign"
                            className="u-select"
                            value=""
                            disabled={routing}
                            onChange={(e) => {
                                const number = assignable.find((n) => n.phone_number === e.target.value);
                                if (number) route(number, agent.id);
                            }}
                        >
                            <option value="">{t('voiceSetup.agent.assignPlaceholder', 'Choisir un numéro…')}</option>
                            {assignable.map((n) => (
                                <option key={n.phone_number} value={n.phone_number}>
                                    {n.agent_name
                                        ? t('voiceSetup.agent.assignTaken', '{{number}} (répond : {{agent}})', { number: numberLabel(n), agent: n.agent_name })
                                        : numberLabel(n)}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>
            )}
        </Card>
    );

    const renderVoice = () => (
        <Card
            icon={AudioLines}
            title={t('voiceUi.agent.voiceTitle', 'Nom et voix')}
            description={t('voiceUi.agent.voiceDesc', 'Comment l’agent sonne. Essayez-le après avoir enregistré.')}
        >
            <div className="u-form">
                <Field label={t('voiceSetup.agents.name', 'Nom')} htmlFor="voice-agent-name">
                    <input
                        id="voice-agent-name"
                        className="u-input"
                        value={form.name}
                        maxLength={80}
                        disabled={!manage}
                        onChange={(e) => set({ name: e.target.value })}
                    />
                </Field>
                <Field label={t('voiceSetup.agent.voice', 'Voix')} htmlFor="voice-agent-voice">
                    <select id="voice-agent-voice" className="u-select" value={form.voice} disabled={!manage} onChange={(e) => set({ voice: e.target.value })}>
                        <option value="">{t('voiceSetup.agent.defaultVoice', 'Par défaut ({{voice}})', { voice: capitalize(DEFAULT_VOICE) })}</option>
                        {voiceOptions.map((v) => <option key={v} value={v}>{capitalize(v)}</option>)}
                    </select>
                </Field>
                <div className="voice-v2__field-pair">
                    <Field label={t('voiceSetup.agent.speed', 'Débit')} htmlFor="voice-agent-speed">
                        <select id="voice-agent-speed" className="u-select" value={form.speed} disabled={!manage} onChange={(e) => set({ speed: e.target.value })}>
                            <option value="">{t('voiceSetup.agent.speedDefault', 'Par défaut')}</option>
                            {speedOptions.map((s) => <option key={s} value={s}>{t(`voiceSetup.agent.speeds.${s.replace('.', '_')}`, SPEED_LABELS[s] || s)}</option>)}
                        </select>
                    </Field>
                    <Field label={t('voiceSetup.agent.language', 'Langue')} htmlFor="voice-agent-language">
                        <select id="voice-agent-language" className="u-select" value={form.language} disabled={!manage} onChange={(e) => set({ language: e.target.value })}>
                            <option value="">{t('voiceSetup.agent.languageAuto', 'Celle de l’appelant')}</option>
                            {languageOptions.map((l) => <option key={l} value={l}>{t(`voiceSetup.agent.languages.${l}`, LANGUAGE_LABELS[l] || l)}</option>)}
                        </select>
                    </Field>
                </div>
                <div className="voice-v2__switch-row">
                    <div>
                        <label className="voice-v2__switch-title" htmlFor="voice-agent-memory">{t('voiceSetup.agent.memory', 'Se souvenir des appelants')}</label>
                        <p className="u-help">{t('voiceSetup.agent.memoryHint', 'L’agent reconnaît un appelant qui rappelle (par son numéro) et reprend le contexte de ses appels précédents.')}</p>
                    </div>
                    <Switch id="voice-agent-memory" checked={form.memory_enabled} disabled={!manage} onChange={(on) => set({ memory_enabled: on })} />
                </div>
            </div>
        </Card>
    );

    const renderCalls = () => (
        <Card
            icon={PhoneCall}
            title={t('voiceSetup.agent.callsTitle', 'Derniers appels')}
            description={tp('voiceUi.agents.scopeCalls', '{{count}} appel sur 30 jours', '{{count}} appels sur 30 jours', { count: agent.calls_30d || 0 })}
            footer={(
                <Link className="u-btn u-btn--ghost u-btn--sm" to="/voice-tickets">
                    {t('voiceSetup.agent.allCalls', 'Tous les appels')}
                    <ChevronRight aria-hidden="true" />
                </Link>
            )}
            flush
        >
            {recentCalls.length === 0 ? (
                <EmptyState icon={PhoneCall} title={t('voiceSetup.agent.noCalls', 'Aucun appel pour l’instant.')}>
                    {t('voiceUi.agent.noCallsHint', 'Appelez un de ses numéros pour l’essayer en vrai.')}
                </EmptyState>
            ) : (
                <ul className="u-list">
                    {recentCalls.map((call) => (
                        <li key={call.call_id}>
                            <Link to={`/voice-tickets/${encodeURIComponent(call.call_id)}`} className="u-row voice-v2__link-row">
                                <div className="u-row__main">
                                    <div className="u-row__title"><span className="u-num">{formatPhone(call.caller) || t('voiceSetup.agent.hiddenCaller', 'Numéro masqué')}</span></div>
                                    <div className="u-row__meta">{formatCallDay(call.started_at)}</div>
                                </div>
                                <div className="u-row__side">
                                    <span className="u-num voice-v2__duration">{formatDuration(call.duration_seconds)}</span>
                                    <ChevronRight className="voice-v2__chevron" aria-hidden="true" />
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );

    return (
        <div className="u-page voice-v2">
            <PageHeader
                breadcrumb={(
                    <>
                        <Link to="/voice-agents">{t('voiceSetup.agents.title', 'Agents vocaux')}</Link>
                        <ChevronRight className="voice-v2__crumb-sep" aria-hidden="true" />
                    </>
                )}
                title={(
                    <span className="voice-v2__title">
                        <span className="voice-v2__title-text">{agent.name || t('voiceSetup.unnamedAgent', 'Agent sans nom')}</span>
                        {agent.numbers.length
                            ? <Badge tone="success" dot>{t('voiceSetup.agents.live', 'En ligne')}</Badge>
                            : <Badge dot>{t('voiceSetup.agents.noNumber', 'Sans numéro')}</Badge>}
                    </span>
                )}
                subtitle={subtitle}
                actions={(
                    <>
                        <Link className="u-btn u-btn--secondary" to="/voice-tickets">
                            <PhoneCall aria-hidden="true" />
                            {t('voiceUi.agent.callsLink', 'Appels')}
                        </Link>
                        {manage && (
                            <OverflowMenu
                                label={t('voiceUi.moreActions', 'Plus d’actions')}
                                items={[{
                                    label: t('voiceSetup.agent.delete', 'Supprimer l’agent'),
                                    icon: Trash2,
                                    danger: true,
                                    onClick: () => setConfirmDelete(true),
                                }]}
                            />
                        )}
                    </>
                )}
            />

            <div className="u-well">
                {agent.migrated_from_text_agent && (
                    <Banner tone="neutral">
                        {t('voiceSetup.agent.migratedHint', 'Repris des réglages « Voix » de l’agent texte du même nom : les deux sont désormais indépendants.')}
                    </Banner>
                )}
                <AttentionCard items={attention} t={t} />
                {renderKpis()}
                <div className="voice-v2__layout">
                    <div className="voice-v2__main">
                        <VoicePreview agentId={agent.id} toolLabel={toolLabel} unsaved={dirty} />
                        {renderSpeech()}
                        {renderTools()}
                    </div>
                    <aside className="voice-v2__aside">
                        {renderNumbers()}
                        {renderVoice()}
                        {renderCalls()}
                    </aside>
                </div>
                {manage && (
                    <SaveBar
                        dirty={dirty}
                        saving={saving}
                        onSave={save}
                        onReset={() => { setForm(saved); setShowProsody(Boolean(saved.prosody)); }}
                        labels={{
                            region: t('voiceUi.agent.saveRegion', 'Enregistrement de l’agent'),
                            dirty: t('voiceSetup.agent.unsaved', 'Modifications non enregistrées'),
                            clean: t('voiceUi.agent.allSaved', 'Tout est enregistré'),
                            reset: t('voiceUi.agent.reset', 'Annuler les modifications'),
                            save: t('voiceSetup.agent.save', 'Enregistrer'),
                            saving: t('voiceSetup.agent.saving', 'Enregistrement…'),
                        }}
                    />
                )}
            </div>

            {confirmTemplate && (
                <ConfirmDialog
                    title={t('voiceSetup.agent.templateConfirm', 'Remplacer vos instructions par le modèle ?')}
                    message={t('voiceUi.agent.templateConfirmText', 'Vos consignes actuelles sont remplacées dans le formulaire. Rien n’est enregistré avant « Enregistrer ».')}
                    confirmLabel={t('voiceSetup.agent.templateReplace', 'Remplacer')}
                    cancelLabel={t('voiceSetup.cancel', 'Annuler')}
                    onConfirm={applyTemplate}
                    onCancel={() => setConfirmTemplate(false)}
                    icon={FileText}
                />
            )}
            {confirmDelete && (
                <ConfirmDialog
                    title={t('voiceUi.agent.deleteTitle', 'Supprimer l’agent {{name}} ?', { name: agent.name })}
                    message={agent.numbers.length
                        ? t('voiceUi.agent.deleteRouted', 'Ses numéros restent sur le compte mais ne répondront plus. Ses appels passés restent consultables.')
                        : t('voiceUi.agent.deleteFree', 'Ses réglages sont perdus. Ses appels passés restent consultables.')}
                    confirmLabel={t('voiceSetup.agent.delete', 'Supprimer l’agent')}
                    cancelLabel={t('voiceSetup.cancel', 'Annuler')}
                    onConfirm={remove}
                    onCancel={() => setConfirmDelete(false)}
                    busy={deleting}
                    icon={Trash2}
                />
            )}
            {unrouting && (
                <ConfirmDialog
                    title={t('voiceUi.agent.unrouteTitle', 'Ne plus répondre sur le {{number}} ?', { number: numberLabel(unrouting) })}
                    message={t('voiceUi.agent.unrouteText', 'Les appels vers ce numéro ne seront plus décrochés tant qu’aucun agent n’y est relié. Le numéro reste sur le compte.')}
                    confirmLabel={t('voiceUi.agent.unrouteDo', 'Ne plus répondre')}
                    cancelLabel={t('voiceSetup.cancel', 'Annuler')}
                    onConfirm={() => route(unrouting, null)}
                    onCancel={() => setUnrouting(null)}
                    busy={routing}
                    icon={PhoneOff}
                />
            )}
            {toast && (
                <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('voiceUi.close', 'Fermer')}>
                    {toast.text}
                </Toast>
            )}
        </div>
    );
};

export default VoiceAgentPage;
